import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface FeaturedSliderProps {
  articles: Article[];
}

export default function FeaturedSlider({ articles }: FeaturedSliderProps) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (articles.length === 0) return;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % articles.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [articles.length]);

  if (articles.length === 0) return null;

  const article = articles[current];

  return (
    <section className="relative w-full h-[420px] md:h-[550px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20"></div>

      <div className="absolute bottom-0 left-0 right-0 w-full px-4 md:px-6 pb-8 md:pb-12">
        <div className="max-w-3xl">
          <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-primary-500 text-background-50 mb-3">
            {article.category}
          </span>
          <Link to={`/article/${article.id}`}>
            <h2 className="text-2xl md:text-4xl font-bold text-background-50 leading-tight hover:text-primary-300 transition-colors">
              {article.title}
            </h2>
          </Link>
          <p className="text-sm md:text-base text-background-50/80 mt-3 line-clamp-2 leading-relaxed max-w-2xl">
            {article.excerpt}
          </p>
          <div className="flex items-center gap-4 mt-4 text-sm text-background-50/60">
            <span>{article.publishDate}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
              {(article.readCount / 10000).toFixed(1)}万阅读
            </span>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 md:right-6 flex items-center gap-2">
        {articles.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrent(idx)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              idx === current
                ? 'w-6 bg-primary-500'
                : 'bg-background-50/50 hover:bg-background-50/80'
            }`}
            aria-label={`Slide ${idx + 1}`}
          />
        ))}
      </div>
    </section>
  );
}